import _debounce from 'debounce';
import * as dom from '../../util/easy-dom';
import {cmptMenu, cmptMenuItem, cmptInputClear, cmptDynamicWidth} from '../../util/cmpt-common-features';
import * as store from '../../util/store';
import * as events from '../../util/event-bus';


/**
 * Displays allowed organizations
 * @class Sidekick.OrganizationSwitcher
 * @memberOf Sidekick
 * @param {EasyDom} elem - Container {@link EasyDom} element
 * @param {Object} attrs
 * @param {Object} ctx - org switcher context object
 * @param {Function} i18n - translation function
 * @emits header.org-switcher.change - Passes selected organization
 */
export default function(elem, attrs, ctx, i18n) {
	const { responsiveTopNav } = store.get('sk-config');
	const hideOrgSwitcher = store.get('sk-config').hideOrgSwitcher;
	const hasPassport = store.get('passport-data');
	if (hideOrgSwitcher && hasPassport !== null) { return; };
	const limit = 10; // minimum value of rows to be present before showing search box
	const listEmpty = dom.parse('<li class="sk-list-empty">No Organizations Found</li>');

	/////
	// find dom elements that we care about
	//
	const [domBtn] = elem.find('.control');
	const [domBtnContent] = domBtn.find('.content');
	const [domOrgCont] = elem.find('.menu');
	const [domOrgList] = elem.find('.menulist');
	const [domSearchCont] = elem.find('.search');
	const [domSearchText] = elem.find('.search-org');
	const [domClearBtn] = elem.find('.ss-ctrl-clear');
	const [domOrgLoadAllBtn] = elem.find('.load-all-btn');
	const domButtonElements = document.querySelectorAll('.organization-btn');
	const [domOrgMobileButton] = elem.find('.control-responsive-org-switcher');
	const [domOrgContMobile] = elem.find('.org-switcher-mobile-menu');
	const [domOrgListMobile] = elem.find('.mobile-org-switcher-menulist');
	const [domOrgSearchContMobile] = elem.find('.mobile-search');
	const [domSearchTextMobile] = elem.find('.search-mobile-org');
	const [domClearBtnMobile] = elem.find('.ss-ctrl-clear-mobile');
	const [domOrgLoadAllBtnMobile] = elem.find('.load-all-btn-mobile');

	/////
	// set up the org-switcher for mobile device
	//
	if(responsiveTopNav) {
		domButtonElements.forEach((domButtonElement) => {
			domButtonElement.classList.add('sk--org-switcher-responsive');
		});

		if(attrs.role === 'mobileitem') {
			domOrgMobileButton.css('display', 'block');
			domOrgMobileButton.on('click', (evt) => {
				evt.stopPropagation();
				evt.preventDefault();
				if (domOrgContMobile.css('display') === 'none') {
					domOrgContMobile.css('display', 'block');
				} else {
					domOrgContMobile.css('display', 'none');
				}
			});
		}
	}

	/////
	// wait to handle this initial state until the current org is specified
	//
	store.onValue('current-org', function (org) {
		ctx.orgs
			.filter(o => o.id.toString() === org.id.toString())
			.filter(o => o.name.startsWith('IDM ADMIN:'))
			.forEach(o => o.name = org.name);

		render();
	});
	render();
	const debouncedFn = _debounce(() => render(), 200);

	/////
	// listen for events and take action
	//
	cmptMenu(elem, domBtn, domOrgCont);
	cmptInputClear(domSearchText, domClearBtn);
	cmptInputClear(domSearchTextMobile, domClearBtnMobile);
	domSearchText.on('input', debouncedFn);
	domSearchTextMobile.on('input', debouncedFn);
	domClearBtn.on('click', () => render());
	domClearBtnMobile.on('click', () => render());
	domOrgLoadAllBtn.on('click', () => render(-1));
	events.on('sk-menu-close', () => {
		if (domOrgLoadAllBtn.css('display') === 'none') {
			render();
		}
	});
	domOrgLoadAllBtnMobile.on('click', () => render(-1));

	/////
	// helper functions
	//

	/**
	 * Filters orgs
	 */
	function searchOrgs(org, searchTextElement) {
		const filter = searchTextElement._.value;
		// no search filter
		if (!filter.length) { return true; }
		// search filter
		return org.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
	}

	/**
	 * Processes the access data to populate the list
	 * @param {number} displayLimit - number of rows to be rendered in DOM
	 */
	function render(displayLimit = 20) {
		const {orgs, currentOrg} = ctx;

		// no access, don't show, we are done
		if (currentOrg == null || orgs.length === 0) {
			elem.css('display', 'none');
			return;
		}
		elem.css('display', 'inline-block');

		// render out our current org name in the header
		const orgName = orgs.find(o => o.id === currentOrg).name;
		domBtn.attr('aria-label', `${orgName} - ${i18n('Switch Organizations')}`);
		domOrgMobileButton.attr('aria-label', `${orgName} - ${i18n('Switch Organizations')}`);
		domBtnContent._.innerHTML = orgName;
		domBtnContent.attr('title', orgName);
		if(!attrs.role === 'mobileitem') {
			cmptDynamicWidth('org-switcher', elem, orgName, 30);
		}

		if (orgs.length === 1) {
			elem.addClass('sk-no-click');
			domBtn.attr('tabindex', '-1');
			domBtn.attr('disabled', 'disabled');
			domBtn.attr('aria-disabled', 'true');

			domOrgMobileButton.attr('tabindex', '-1');
			domOrgMobileButton.attr('disabled', 'disabled');
			domOrgMobileButton.attr('aria-disabled', 'true');

			domBtnContent.removeClass('fa-angle-down-after');
			domOrgCont.css('display', 'none');
			domOrgContMobile.css('display', 'none');
			// no need to continue, we only have 1 org. No picker is needed.
			return;
		} else {
			elem.removeClass('sk-no-click');
			domBtn.attr('tabindex', '0');
			domBtn.attr('disabled', null);
			domBtn.attr('aria-disabled', 'false');

			domOrgMobileButton.attr('tabindex', '0');
			domOrgMobileButton.attr('disabled', null);
			domOrgMobileButton.attr('aria-disabled', 'false');

			domBtnContent.addClass('fa-angle-down-after');
			domSearchCont.css('display', orgs.length > limit ? 'block' : 'none');
			domOrgSearchContMobile.css('display', orgs.length > limit ? 'block' : 'none');
		}

		// Sort by name
		orgs.sort((a, b) => {
			return a.name.localeCompare(b.name);
		});

		const searchResultsOrgList = orgs.filter(org => searchOrgs(org, domSearchText));
		const searchResultsOrgListMobile = orgs.filter(org => searchOrgs(org, domSearchTextMobile));

		createOrgList(domOrgList, domBtn, searchResultsOrgList, displayLimit, currentOrg);
		createOrgList(domOrgListMobile, domOrgMobileButton, searchResultsOrgListMobile, displayLimit, currentOrg);

		if (searchResultsOrgList.length <= displayLimit || displayLimit < 0) {
			domOrgLoadAllBtn.css('display', 'none');
		} else {
			domOrgLoadAllBtn.css('display', 'block');
		}

		if(searchResultsOrgListMobile.length <= displayLimit || displayLimit < 0) {
			domOrgLoadAllBtnMobile.css('display', 'none');
		} else {
			domOrgLoadAllBtnMobile.css('display', 'block');
		}
	}

	/**
	 * Creates the list of organizations
	 * @param {EasyDom} domList - List element
	 * @param {EasyDom} domButton - Button element
	 * @param {Array} searchResults - Filtered list of organizations
	 */
	function createOrgList(domList, domButton, searchResults, displayLimit, currentOrg) {
		if (searchResults.length > 0) {
			domList.children(searchResults.slice(0, displayLimit < 0 ? searchResults.length : displayLimit).map(org => {
				const isActive = org.id === currentOrg;

				const ariaCurrent = isActive.toString();
				const liClassName = isActive ? 'active' : '';
				const linkClassName = isActive ? 'fa-li-checkmark-after active' : '';

				const href = org.url || '#';
				const target = (org.url && org.target ? ` target="${org.target}"` : '');

				const el = dom.parse(`
					<li class="${liClassName}">
						<a role="menuitem" aria-current="${ariaCurrent}" aria-selected="false" class="active-state-link ${linkClassName}" href="${href}"${target}>${org.name}</a>
					</li>
				`);

				el.on('click', evt => {
					if (!org.url) {
						evt.stopPropagation();
						evt.preventDefault();
						events.emit('header.org-switcher.change', org);
					}
					events.emit('sk-menu-close', elem);
				});

				// bind common keydown handlers to menu item
				cmptMenuItem(elem, domButton, domList, el);

				return el;
			}));
		} else {
			domList.children(listEmpty);
		}
	}
};
